import { Component, Inject, OnInit } from '@angular/core';
import { DateAdapter, MatDatepickerInputEvent, MatDialogRef, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_DIALOG_DATA } from '@angular/material';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import moment from 'moment';
import { formatDay, parseDays, toDaysText } from '../paid-holiday-days';

/**
 * 呼び出し元（有休5日使用義務リスト）から渡されるデータ
 */
export interface DaysEditDialogData {
  /** リストの行 */
  row: any;
  /** 指定年月（YYYY-MM-DD） */
  targetYearMonth: string;
  /** 操作ユーザー名（備考への変更履歴に記載） */
  updateUser: string;
}

/**
 * 保存ボタンで呼び出し元へ返す編集結果（保存自体は呼び出し元が行う）
 */
export interface DaysEditDialogResult {
  /** 指定日候補（"{2026-08-30,2026-09-06}"形式。なしはnull） */
  days: string;
  /** 指定日候補を変更したか */
  isDaysEdited: boolean;
  /** 備考に追記する変更履歴（変更がなければnull） 例："9/1(火)、9/2(水)➡9/10(木)、9/11(金)（shimazoe）" */
  history: string;
}

/**
 * 編集中の候補日1件
 * 日付入力欄にそのまま渡すため、moment型で保持する（未入力はnull）
 */
interface DayItem {
  value: moment.Moment;
}

export const DATE_FORMATS = {
  parse: {
    dateInput: 'YYYY/MM/DD',
  },
  display: {
    dateInput: 'YYYY/MM/DD',
    monthYearLabel: 'YYYY年MM月',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY年MM月',
  },
};

/**
 * 「指定日候補」の編集ダイアログ
 * 候補日の件数は使用義務残数に合わせて算出済みのため、基本は既存の日付を差し替える操作にし、
 * 件数の増減（追加・削除）はどうしても必要な場合だけボタンで行う
 * 備考は一覧の表で直接編集するため、このダイアログでは扱わない
 */
@Component({
  selector: 'app-days-edit-dialog',
  templateUrl: './days-edit-dialog.component.html',
  styleUrls: ['./days-edit-dialog.component.less'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    {provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS},
  ]
})
export class DaysEditDialogComponent implements OnInit {
  // 編集中の候補日
  items: DayItem[] = [];

  // 開いた時点の候補日（YYYY-MM-DD、変更の有無の判定と備考への履歴追記に使う）
  originalDays: string[] = [];

  // 日付入力欄のカレンダーの初期表示月（候補日があればその月、なければ指定年月）
  startAt: moment.Moment;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DaysEditDialogData,
    private dialogRef: MatDialogRef<DaysEditDialogComponent>
  ) { }

  ngOnInit() {
    this.originalDays = parseDays(this.data.row.days);
    this.items = this.originalDays.map(d => ({ value: moment(d, 'YYYY-MM-DD') }));
    this.startAt = this.originalDays.length > 0
      ? moment(this.originalDays[0], 'YYYY-MM-DD')
      : moment(this.data.targetYearMonth, 'YYYY-MM-DD');
  }

  /**
   * 使用義務残数（"3日" → 3）
   */
  get obligationCount(): number {
    let n = parseInt(String(this.data.row.remainingUseObligation || ''), 10);
    return isNaN(n) ? 0 : n;
  }

  /**
   * 候補日が使用義務残数に足りていない場合true（リストの要注意判定と同じ条件）
   */
  get isShort(): boolean {
    return this.items.length < this.obligationCount;
  }

  /**
   * 編集中の候補日をYYYY-MM-DDの配列にする（未入力は除く、昇順）
   */
  private currentDays(): string[] {
    return this.items
      .filter(item => item.value && item.value.isValid())
      .map(item => item.value.format('YYYY-MM-DD'))
      .sort();
  }

  /**
   * 候補日を変更したか
   */
  get isDaysChanged(): boolean {
    return this.currentDays().join(',') != this.originalDays.join(',');
  }

  /**
   * 候補日の曜日表示（入力欄の横に出す）
   * @param item 候補日
   */
  weekday(item: DayItem): string {
    if (!item.value || !item.value.isValid()) {
      return '';
    }
    return '(' + ['日', '月', '火', '水', '木', '金', '土'][item.value.day()] + ')';
  }

  /**
   * 日付入力欄で日付を変更した
   * @param item 候補日
   * @param event 変更後の日付
   */
  changeDay(item: DayItem, event: MatDatepickerInputEvent<moment.Moment>) {
    item.value = event.value ? moment(event.value) : null;
  }

  /**
   * 「候補日を追加」ボタン（件数を増やしたい場合のみ）
   * 未入力の行を1件追加し、日付は入力欄から選んでもらう
   */
  addDay() {
    this.items.push({ value: null });
  }

  /**
   * 候補日の削除ボタン（件数を減らしたい場合のみ）
   * @param index 削除する行
   */
  removeDay(index: number) {
    this.items.splice(index, 1);
  }

  /**
   * 備考に追記する候補日の変更履歴
   * 開いた時点の候補日と保存する候補日を比較し、変わった日付だけを「変更前➡変更後（ユーザー名）」の形にする
   * 変わっていない日付は書かない
   *   差し替え：9/1(火)➡9/10(木)（shimazoe）
   *   2件以上：9/1(火)、9/2(水)➡9/10(木)、9/11(金)（shimazoe）
   *   追加のみ：9/10(木)追加（shimazoe）
   *   削除のみ：9/1(火)削除（shimazoe）
   * @param days 保存する候補日（YYYY-MM-DD、昇順）
   * @returns 追記する1行。変更がなければnull
   */
  private buildHistory(days: string[]): string {
    let removed = this.originalDays.filter(d => days.indexOf(d) < 0).map(d => formatDay(d)).join('、');
    let added = days.filter(d => this.originalDays.indexOf(d) < 0).map(d => formatDay(d)).join('、');
    if (!removed && !added) {
      return null;
    }
    let history: string;
    if (removed && added) {
      history = removed + '➡' + added;
    } else if (added) {
      history = added + '追加';
    } else {
      history = removed + '削除';
    }
    return history + '（' + (this.data.updateUser || '') + '）';
  }

  /**
   * 保存ボタンイベント
   * 入力チェックのうえ編集結果を呼び出し元へ返す。
   * 保存（初回・TEL・2回目以降と同じ行単位の保存API）とリストの再取得は呼び出し元で行う
   */
  save() {
    if (this.items.some(item => !item.value || !item.value.isValid())) {
      alert("日付が未入力の候補日があります");
      return;
    }
    // 同じ日付が2件以上ある場合は保存しない（重なっている日付を示す）
    let days = this.currentDays();
    let duplicated = days.filter((d, i) => days.indexOf(d) != i);
    if (duplicated.length > 0) {
      let list = Array.from(new Set(duplicated)).map(d => formatDay(d)).join('、');
      alert(list + "が複数あります。変更してください。");
      return;
    }

    // 保存時に日付順（昇順）に並べ替える（currentDaysでソート済み）
    let history = this.buildHistory(days);
    let result: DaysEditDialogResult = {
      days: toDaysText(days),
      isDaysEdited: history != null,
      history: history
    };
    this.dialogRef.close(result);
  }

  /**
   * キャンセルボタンイベント
   */
  cancel() {
    this.dialogRef.close();
  }
}
